import type { PublicacaoDto } from "@base/contracts";

/**
 * Texto curto de quanto falta (ou quanto passou) até um horário.
 * "agora", "em 15 min", "em 3 h", "amanhã", "em 4 dias", "há 2 dias".
 */
export function tempoRelativo(alvo: Date, agora: Date): string {
  const diffMs = alvo.getTime() - agora.getTime();
  const futuro = diffMs >= 0;
  const abs = Math.abs(diffMs);
  const minutos = Math.round(abs / 60_000);
  if (minutos < 1) return "agora";
  const dias = diasDeCalendario(agora, alvo);
  // Outro dia de calendário fala em dias ("amanhã"), mesmo faltando < 24h.
  if (dias === 0 || minutos < 60) {
    if (minutos < 60) return futuro ? `em ${minutos} min` : `há ${minutos} min`;
    const horas = Math.round(minutos / 60);
    return futuro ? `em ${horas} h` : `há ${horas} h`;
  }
  if (dias === 1) return "amanhã";
  if (dias === -1) return "ontem";
  return futuro ? `em ${Math.abs(dias)} dias` : `há ${Math.abs(dias)} dias`;
}

/** Diferença em dias de calendário (meia-noite a meia-noite, fuso local). */
function diasDeCalendario(de: Date, ate: Date): number {
  const a = new Date(de.getFullYear(), de.getMonth(), de.getDate()).getTime();
  const b = new Date(ate.getFullYear(), ate.getMonth(), ate.getDate()).getTime();
  return Math.round((b - a) / 86_400_000);
}

export type EstadoDaEtapa = "feita" | "atual" | "pendente" | "erro";

export type EtapaDaPublicacao = {
  readonly chave: "criado" | "fila" | "publicado";
  readonly rotulo: string;
  readonly estado: EstadoDaEtapa;
  readonly quando: string | null;
};

/**
 * Linha do tempo do post: Criado → Na fila → Publicado.
 * Falha marca a etapa final como erro (o motivo aparece à parte).
 */
export function etapasDaPublicacao(
  p: Pick<PublicacaoDto, "status" | "criadoEm" | "agendarPara" | "publicadoEm">,
): readonly EtapaDaPublicacao[] {
  const naFila = p.status === "queued" || p.status === "scheduled";
  const publicado = p.status === "publicado";
  const falhou = p.status === "falhou";
  const passouDaFila = naFila || publicado || falhou;
  return [
    { chave: "criado", rotulo: "Criado", estado: "feita", quando: p.criadoEm },
    {
      chave: "fila",
      rotulo: "Na fila",
      estado: publicado || falhou ? "feita" : naFila ? "atual" : "pendente",
      quando: passouDaFila ? p.agendarPara : null,
    },
    {
      chave: "publicado",
      rotulo: falhou ? "Falhou" : "Publicado",
      estado: publicado ? "feita" : falhou ? "erro" : "pendente",
      quando: publicado ? p.publicadoEm : null,
    },
  ];
}
